'use client'

import { useRef, useEffect, useState } from 'react'

import investorsStyles from '../investors.module.scss'
import { basePath } from '@lib/constants'

// Optimize video loading - only load when in viewport
const useIntersectionObserver = (ref: React.RefObject<HTMLVideoElement>, options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting)
    }, options)

    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, options])

  return isIntersecting
}

type VideoData = {
  src: string
  poster: string
  title?: string
  description: string
}

const buildVideoSrc = (fileName: string): string => encodeURI(`${basePath}/videos/${fileName}.webm`)
const buildPosterSrc = (fileName: string): string => encodeURI(`${basePath}/videos/mini/${fileName}.jpg`)

export default function HeroVideos() {
  const mainVideoRef = useRef<HTMLVideoElement>(null)
  const thumb1Ref = useRef<HTMLVideoElement>(null)
  const thumb2Ref = useRef<HTMLVideoElement>(null)
  const modalVideoRef = useRef<HTMLVideoElement>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null)

  // Use intersection observer for lazy loading
  const isMainVideoVisible = useIntersectionObserver(mainVideoRef, { threshold: 0.1 })
  const isThumb1Visible = useIntersectionObserver(thumb1Ref, { threshold: 0.1 })
  const isThumb2Visible = useIntersectionObserver(thumb2Ref, { threshold: 0.1 })

  useEffect(() => {
    // Auto-play videos only when they're visible in viewport, pause when not visible
    if (mainVideoRef.current) {
      if (isMainVideoVisible) {
        mainVideoRef.current.play().catch(() => {
          // Autoplay was prevented, ignore error
        })
      } else {
        mainVideoRef.current.pause()
      }
    }
    if (thumb1Ref.current) {
      if (isThumb1Visible) {
        thumb1Ref.current.play().catch(() => {
          // Autoplay was prevented, ignore error
        })
      } else {
        thumb1Ref.current.pause()
      }
    }
    if (thumb2Ref.current) {
      if (isThumb2Visible) {
        thumb2Ref.current.play().catch(() => {
          // Autoplay was prevented, ignore error
        })
      } else {
        thumb2Ref.current.pause()
      }
    }
  }, [isMainVideoVisible, isThumb1Visible, isThumb2Visible])

  useEffect(() => {
    // Play video in modal when it opens
    if (isModalOpen && modalVideoRef.current) {
      modalVideoRef.current.play().catch(() => {
        // Autoplay was prevented, ignore error
      })
    }
  }, [isModalOpen])

  useEffect(() => {
    // Close modal on Escape key
    if (!isModalOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isModalOpen])

  const openModal = (videoData: VideoData) => {
    setSelectedVideo(videoData)
    setIsModalOpen(true)
    document.body.style.overflow = 'hidden' // Prevent background scrolling
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedVideo(null)
    document.body.style.overflow = 'unset' // Restore scrolling
    if (modalVideoRef.current) {
      modalVideoRef.current.pause()
      modalVideoRef.current.currentTime = 0
    }
  }

  const handleVideoClick = (videoData: VideoData) => {
    openModal(videoData)
  }

  const mainVideo = {
    src: buildVideoSrc('KohlerAIAgent'),
    poster: buildPosterSrc('Alice&Rob'),
    description: 'Kohler AI Agent',
  }

  const secondVideo = {
    src: buildVideoSrc('KohlerSonicApplySidebySide'),
    poster: buildPosterSrc('Alice&Rob'),
    description: 'Kohler SonicApply Side-by-Side',
  }

  const thirdVideo = {
    src: buildVideoSrc('ChatGPTSonicApply'),
    poster: buildPosterSrc('RobotEngineReport'),
    description: 'ChatGPT SonicApply',
  }

  return (
    <>
      <div className={investorsStyles.heroMedia}>
        <div className={investorsStyles.heroVideoMain} onClick={() => handleVideoClick(mainVideo)}>
          <video
            ref={mainVideoRef}
            className={investorsStyles.heroVideo}
            poster={mainVideo.poster}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={mainVideo.src} type="video/webm" />
          </video>
          <div className={investorsStyles.heroVideoMainIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={investorsStyles.heroVideoMainPlay}
            >
              <polygon points="6 3 20 12 6 21 6 3"></polygon>
            </svg>
          </div>
        </div>
        <div className={investorsStyles.heroVideoThumb} onClick={() => handleVideoClick(secondVideo)}>
          <video
            ref={thumb1Ref}
            className={investorsStyles.heroVideo}
            poster={secondVideo.poster}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={secondVideo.src} type="video/webm" />
          </video>
          <div className={investorsStyles.heroVideoThumbIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={investorsStyles.heroVideoThumbPlay}
            >
              <polygon points="6 3 20 12 6 21 6 3"></polygon>
            </svg>
          </div>
        </div>
        <div className={investorsStyles.heroVideoThumb} onClick={() => handleVideoClick(thirdVideo)}>
          <video
            ref={thumb2Ref}
            className={investorsStyles.heroVideo}
            poster={thirdVideo.poster}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={thirdVideo.src} type="video/webm" />
          </video>
          <div className={investorsStyles.heroVideoThumbIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={investorsStyles.heroVideoThumbPlay}
            >
              <polygon points="6 3 20 12 6 21 6 3"></polygon>
            </svg>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isModalOpen && selectedVideo && (
        <div className={investorsStyles.videoModal} onClick={closeModal}>
          <div className={investorsStyles.videoModalContent} onClick={(e) => e.stopPropagation()}>
            <button className={investorsStyles.videoModalClose} onClick={closeModal} aria-label="Close video">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <video
              ref={modalVideoRef}
              className={investorsStyles.videoModalPlayer}
              src={selectedVideo.src}
              poster={selectedVideo.poster}
              controls
              controlsList="nodownload"
              autoPlay
              loop
              preload="auto"
            >
              <source src={selectedVideo.src} type={selectedVideo.src.endsWith('.mp4') ? 'video/mp4' : 'video/webm'} />
            </video>
            {selectedVideo.description && (
              <div className={investorsStyles.videoModalDescription}>
                <p>{selectedVideo.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
